import { Inject } from '@odinjs/odin';
import { DatePickerAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnChange, OnClick, OnMount, OnNavigate } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableNavigateEvent, ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { ISO_DATE_FORMAT, ISO_YEAR_MONTH_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import moment from 'moment/moment.js';
import { FragmentNames, PseudonymAppointmentsStep_6Aliases } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import PseudonymAppointmentsBaseController from './pseudonym-appointments-base-controller';
import PseudonymAppointmentModel from './pseudonym-appointments-model';
import PseudonymSchedulingWaitingListService from './pseudonym-scheduling-waiting-list/pseudonym-scheduling-waiting-list-service';
import PseudonymIntegratedScheduleService from './pseudonym-service/pseudonym-integrated-schedule-service';
import { AppointmentsSteps } from './pseudonym-types';
import PseudonymStepSixService from './pseudonym-service/pseudonym-step-6-service';
interface SchedulingConfig {
  allowSelectDesiredDate: boolean;
  desiredDateCustomMessage: string;
  scheduleRangeAllowed: number;
  schedulingAllowedDays: number;
  schedulingFinalDate: Date;
}
@Controller({ component: PseudonymAppointmentsStep_6Aliases.LAYOUT_SCHEDULE_PERIOD, fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_STEP_6 })
export default class PseudonymAppointmentsStep6Controller extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymAppointmentsStep_6Aliases.PANEL_NOTIFICATION_SCHEDULE_PERIOD)
    panelNotificationSchedulePeriodApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_6Aliases.DATE_PICKER_SCHEDULE_RANGE)
    datePickerScheduleRange!: DatePickerAPI;

  @Inject('PseudonymIntegratedScheduleService')
    pseudonymIntegratedScheduleService!: PseudonymIntegratedScheduleService;

  @Inject('PseudonymSchedulingWaitingListService')
    pseudonymSchedulingWaitingListService!: PseudonymSchedulingWaitingListService;

  @Inject('PseudonymStepSixService')
    pseudonymStepSixService!: PseudonymStepSixService;

  @OnMount({ target: PseudonymAppointmentsStep_6Aliases.PANEL_NOTIFICATION_SCHEDULE_PERIOD })
  handleMountPanelNotificationSchedulePeriod(): void {
    this.panelNotificationSchedulePeriodApi.setVisible(false);
    PseudonymAppointmentModel.stepSix.panelNotificationSchedulePeriodApi = this.panelNotificationSchedulePeriodApi
    PseudonymAppointmentModel.stepSix.datePickerScheduleRange = this.datePickerScheduleRange;
    PseudonymAppointmentModel.stepSix.verifyAvailableCalendarDays = () => this.verifyAvailableCalendarDays();
  }

  @OnMount({ target: PseudonymAppointmentsStep_6Aliases.DATE_PICKER_SCHEDULE_RANGE })
  handleMountDatePicker(): void {
    HttpService.post(APPLICATION_ENDPOINTS.GET_NEW_APPOINTMENT_SCHEDULE_CONFIG)
      .then(response => {
        const schedulingConfig = response.body as SchedulingConfig;
        const today = moment().utc().toISOString();
        const finalDate = schedulingConfig.schedulingFinalDate.toString();

        this.datePickerScheduleRange.setIntervalThreshold(schedulingConfig.scheduleRangeAllowed);
        this.datePickerScheduleRange.addRange(today, finalDate);
        this.setInitialRangeDatePickerToContext(today, finalDate);

        PseudonymAppointmentModel.stepSix.selectedDateRange.start = today;
        PseudonymAppointmentModel.stepSix.selectedDateRange.end = finalDate;
        PseudonymAppointmentModel.stepSix.allowSelectDesiredDate = schedulingConfig.allowSelectDesiredDate;
        PseudonymAppointmentModel.stepSix.desiredDateCustomMessage = schedulingConfig.desiredDateCustomMessage;

        if (this.getIsRescheduleContext() && !PseudonymAppointmentModel.stepFive.location) {
          this.setAvailableCalendarDaysForReschedule();
        }
      });
  }

  private setAvailableCalendarDaysForReschedule(): void {
    this.clearValueDatePickerScheduleRange();
    this.verifyAvailableCalendarDays();
  }

  @OnChange({ target: PseudonymAppointmentsStep_6Aliases.DATE_PICKER_SCHEDULE_RANGE })
  handleChangeDateRange(event: ChangeEvent<Array<string>>): void {
    if (event && event.value) {
      const rangeDate = event.value || [];
      const startDate = rangeDate[0];
      const endDate = rangeDate.length === 1 ? rangeDate[0] : rangeDate[1];

      if (rangeDate.length >= 1) {
        PseudonymAppointmentModel.stepSix.selectedDateRange.start = startDate > endDate ? endDate : startDate;
        PseudonymAppointmentModel.stepSix.selectedDateRange.end = startDate > endDate ? startDate : endDate;
        this.setWizardStepDone(AppointmentsSteps.STEP_6_SCHEDULE_PERIOD, true);
      } else {
        this.setWizardStepDone(AppointmentsSteps.STEP_6_SCHEDULE_PERIOD, false);
      }
    }
  }

  @OnNavigate({ target: PseudonymAppointmentsStep_6Aliases.DATE_PICKER_SCHEDULE_RANGE })
  handleNavigateDatePicker(event: AbortableNavigateEvent<string>): void {
    const currentDate = moment.utc();
    const goToDate = moment.utc(event.to && event.to[0]);
    const lastCalendarDate = goToDate.add(1, 'months');

    if (lastCalendarDate > currentDate) {
      const ranges = this.datePickerScheduleRange.getRanges();
      for (const range of ranges) {
        if (range.startDate.includes(lastCalendarDate.format(ISO_YEAR_MONTH_FORMAT))) {
          return;
        }
      }

      this.verifyAvailableCalendarDays(lastCalendarDate.format(ISO_DATE_FORMAT));
    }
  }

  verifyAvailableCalendarDays(lastCalendarDate = ''): void {
    if (this.panelNotificationSchedulePeriodApi) {
      this.panelNotificationSchedulePeriodApi.setVisible(false);
    }

    const stepTwo = PseudonymAppointmentModel.stepTwo;
    const procedure = stepTwo.procedures[0];
    if (!stepTwo.isOnlyConsultation
      || stepTwo.procedures.length !== 1
      || !procedure.specialtySequence
      || !procedure.physicianSequence) {
      this.pseudonymStepSixService.restoreInitialRangeDatePickerFromContext();
      return;
    }

    const datePickerScheduleRange = PseudonymAppointmentModel.stepSix.datePickerScheduleRange;
    datePickerScheduleRange.clearRanges();
    datePickerScheduleRange.setLoading(true);

    this.pseudonymStepSixService.getAvailableCalendarDays(lastCalendarDate)
      .then(response => this.pseudonymStepSixService.handleAvailableCalendarDaysResponse(response))
      .catch(() => this.pseudonymStepSixService.restoreInitialRangeDatePickerFromContext())
      .finally(() => datePickerScheduleRange.setLoading(false));
  }

  private clearValueDatePickerScheduleRange(): void {
    this.datePickerScheduleRange.setInitialDate(moment().utc().toISOString());
    this.datePickerScheduleRange.setValue([]);
  }

  private setInitialRangeDatePickerToContext(initialDate: string, finalDate: string): void {
    this.context.set({
      finalDate: finalDate,
      initialDate: initialDate,
    });
  }

  @OnClick({ target: PseudonymAppointmentsStep_6Aliases.LINK_CALL_WAITING_LIST })
  handleClickLinkCallWaitingList(): void {
    const integratedScheduleItemSequence = this.context.get('integratedScheduleItemSequence') as string;
    this.pseudonymSchedulingWaitingListService.showPseudonymWaitingListModal(integratedScheduleItemSequence);
  }
}
